import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isMobileMenuOpen]);
  // Close dropdowns on route change
  useEffect(() => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  const navLinks = [{
    name: 'Services',
    subItems: [{
      name: 'Our Services',
      path: '/services'
    }, {
      name: 'Booking & Pricing',
      path: '/pricing'
    }]
  }, {
    name: 'About',
    subItems: [{
      name: 'Who We Are',
      path: '/about'
    }, {
      name: 'Who We Serve',
      path: '/who-we-serve'
    }]
  }, {
    name: 'Resources',
    path: '/resources'
  }, {
    name: 'Contact Us',
    path: '/contact'
  }];
  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (subItems?: {
    path: string;
  }[]) => subItems?.some(item => location.pathname === item.path);
  return <>
      <header role="banner" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-white shadow-lg shadow-slate-200/40 py-3 border-b border-slate-100' : 'bg-transparent py-6'}`} data-id="element-129">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-130">
          <div className="flex justify-between items-center" data-id="element-131">
            <Link to="/" className="flex items-center group" aria-label="Ambient Fingerprinting - Home" data-id="element-132">
              <img src="https://cdn.magicpatterns.com/uploads/45tD6GiCQLAvjHvfZnL4vs/cropped-Media__1_-removebg-preview.png" alt="Ambient Fingerprinting" className="h-10 md:h-12 w-auto object-contain transition-all duration-300 group-hover:scale-105" style={{
              filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.05)) brightness(0.95)'
            }} data-id="element-133" />
            </Link>

            <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation" data-id="element-134">
              {navLinks.map(link => <div key={link.name} className="relative group/nav" onMouseEnter={() => setActiveDropdown(link.name)} onMouseLeave={() => setActiveDropdown(null)} data-id="element-135">
                  {link.subItems ? <button className={`flex items-center gap-1.5 text-sm font-bold tracking-tight transition-all duration-300 ${isParentActive(link.subItems) ? 'text-[#4AA6CF]' : 'text-slate-900 hover:text-[#4AA6CF]'}`} aria-expanded={activeDropdown === link.name} aria-haspopup="true" data-id="element-136">
                      {link.name}
                      <ChevronDownIcon className={`h-4 w-4 transition-transform duration-300 ${activeDropdown === link.name ? 'rotate-180' : ''} ${isParentActive(link.subItems) ? 'text-[#4AA6CF]' : 'text-slate-400 group-hover/nav:text-[#4AA6CF]'}`} data-id="element-137" />
                    </button> : <Link to={link.path!} aria-current={isActive(link.path!) ? 'page' : undefined} className={`text-sm font-bold tracking-tight transition-all duration-300 ${isActive(link.path!) ? 'text-[#4AA6CF] relative after:absolute after:bottom-[-4px] after:left-0 after:right-0 after:h-0.5 after:bg-[#4AA6CF] after:rounded-full' : 'text-slate-900 hover:text-[#4AA6CF]'}`} data-id="element-138">
                      {link.name}
                    </Link>}

                  {/* Desktop Dropdown */}
                  <AnimatePresence data-id="element-139">
                    {link.subItems && activeDropdown === link.name && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.2
                }} className="absolute left-0 mt-4 w-56 bg-white border border-slate-100 rounded-2xl shadow-2xl shadow-slate-200/50 overflow-hidden py-3" data-id="element-140">
                        {link.subItems.map(subItem => <Link key={subItem.path} to={subItem.path} className={`block px-6 py-3 text-sm font-bold transition-all ${isActive(subItem.path) ? 'bg-[#4AA6CF]/10 text-[#4AA6CF]' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-141">
                            {subItem.name}
                          </Link>)}
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
              <Link to="/booking" className="bg-[#4AA6CF] hover:bg-[#3b85a6] text-white px-6 py-2.5 rounded-full text-sm font-bold transition-all shadow-md hover:shadow-lg" data-id="element-142">
                Book Appointment
              </Link>
            </nav>

            <button className="lg:hidden text-[#1E1F55] p-2 hover:text-[#4AA6CF] transition-colors" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open navigation menu" aria-expanded={isMobileMenuOpen} aria-controls="mobile-nav" data-id="element-143">
              <MenuIcon className="h-6 w-6" aria-hidden="true" data-id="element-144" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Nav Panel */}
      <AnimatePresence data-id="element-145">
        {isMobileMenuOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[60]" onClick={() => setIsMobileMenuOpen(false)} aria-hidden="true" data-id="element-146" />
            <motion.div id="mobile-nav" role="dialog" aria-modal="true" aria-label="Navigation menu" initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed top-0 right-0 bottom-0 w-full sm:w-3/4 max-w-md border-l border-slate-100 z-[70] shadow-2xl flex flex-col bg-white" data-id="element-147">
              <div className="p-5 flex justify-between items-center border-b border-gray-100" data-id="element-148">
                <img src="https://cdn.magicpatterns.com/uploads/45tD6GiCQLAvjHvfZnL4vs/cropped-Media__1_-removebg-preview.png" alt="Ambient Fingerprinting" className="h-8 w-auto object-contain" data-id="element-149" />
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-3 text-slate-400 hover:text-slate-900 transition-colors bg-slate-50 rounded-xl" aria-label="Close navigation menu" data-id="element-150">
                  <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-151" />
                </button>
              </div>
              <nav className="flex flex-col p-5 gap-2 overflow-y-auto" aria-label="Mobile navigation" data-id="element-152">
                {navLinks.map(link => <div key={link.name} className="flex flex-col" data-id="element-153">
                    {link.subItems ? <>
                        <button onClick={() => setActiveDropdown(activeDropdown === link.name ? null : link.name)} className="flex items-center justify-between font-bold text-xl py-4 border-b border-gray-50 text-slate-900" data-id="element-154">
                          {link.name}
                          <ChevronDownIcon className={`h-5 w-5 transition-transform ${activeDropdown === link.name ? 'rotate-180' : ''}`} data-id="element-155" />
                        </button>
                        <AnimatePresence data-id="element-156">
                          {activeDropdown === link.name && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} className="overflow-hidden bg-slate-50 rounded-2xl mt-2" data-id="element-157">
                              {link.subItems.map(subItem => <Link key={subItem.path} to={subItem.path} className={`block px-6 py-4 text-base font-bold transition-colors ${isActive(subItem.path) ? 'text-[#4AA6CF]' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => setIsMobileMenuOpen(false)} data-id="element-158">
                                  {subItem.name}
                                </Link>)}
                            </motion.div>}
                        </AnimatePresence>
                      </> : <Link to={link.path!} aria-current={isActive(link.path!) ? 'page' : undefined} className={`font-bold text-xl py-4 border-b border-gray-50 transition-colors ${isActive(link.path!) ? 'text-[#4AA6CF]' : 'text-slate-900 hover:text-[#4AA6CF]'}`} onClick={() => setIsMobileMenuOpen(false)} data-id="element-159">
                        {link.name}
                      </Link>}
                  </div>)}
                <Link to="/booking" className="mt-10 bg-[#4AA6CF] text-white text-center py-5 rounded-full font-extrabold text-lg shadow-xl shadow-[#4AA6CF]/20" onClick={() => setIsMobileMenuOpen(false)} data-id="element-160">
                  Book Appointment
                </Link>
              </nav>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}